// Layer 3 (Transformation): polish.P9 (#657) — the route's pill tabs under the
// flowsheet (locked comp `metal-accounting.html` A, `tabsFor('A')`): Value
// (GM only, first) · Mass balance · Mine to shipped · Period close (MTD only).
// The persona lens stays with the TopBar picker: the GM opens on Value, every
// other persona on Mass balance. Each tab carries its headline before it is
// opened. Pure — no React.

import type { Persona } from '@/shared/types';
import type { AccountingPeriod } from '@/versions/v1/schema/metal-accounting';
import type { SurfacePageTabItem } from '@/design-system';
import {
  allChainReads,
  periodClosePanel,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { ledgerStreamCount } from './MassBalanceLedger.transform';
import { valueTabView } from './ValueTab.transform';

export type MetalAccountingTab = 'value' | 'ledger' | 'chain' | 'close';

const TAB_IDS: readonly MetalAccountingTab[] = ['value', 'ledger', 'chain', 'close'];

export const ACCOUNTING_PERIODS: readonly { id: AccountingPeriod; label: string }[] = [
  { id: 'shift', label: 'Shift' },
  { id: 'day', label: '24 h' },
  { id: 'wtd', label: 'WTD' },
  { id: 'mtd', label: 'MTD' },
];

export function accountingPeriodFromParam(value: string | null): AccountingPeriod {
  return value === 'shift' || value === 'day' || value === 'wtd' || value === 'mtd' ? value : 'day';
}

export function isMetalAccountingTab(value: string | null | undefined): value is MetalAccountingTab {
  return value != null && (TAB_IDS as readonly string[]).includes(value);
}

const MONTH_LABEL: Readonly<Record<string, string>> = { 'may-2026': 'May', 'june-2026-mtd': 'June' };

/** komatsu.F11 (#737): this route's strip already prints each recovery figure
 *  (the Pb/Ag and Zn cells lead with it, against design), so the roll-up band
 *  above it drops those two metrics — one recovery figure per screen. The band
 *  keeps `rollup-feed` and the bottleneck; the Plant model's band, which has no
 *  strip under it, keeps its full four-cell anatomy. */
export const MA_BAND_OMIT_METRICS: readonly string[] = ['rollup-zn-rec', 'rollup-pb-rec'];

function periodCloseCount(view: PeriodView): string | undefined {
  const panel = periodClosePanel(view);
  const open = panel?.rows.find((row) => row.state !== 'closed');
  return open ? `${MONTH_LABEL[open.month] ?? open.month} open` : undefined;
}

/** The tab row for this persona and period. */
export function metalAccountingTabs(persona: Persona, view: PeriodView): readonly SurfacePageTabItem<MetalAccountingTab>[] {
  const tabs: SurfacePageTabItem<MetalAccountingTab>[] = [];
  if (persona === 'GM') {
    const value = valueTabView(view);
    tabs.push({ id: 'value', label: 'Value', count: value ? `${value.total.nsr} NSR` : '—' });
  }
  tabs.push({ id: 'ledger', label: 'Mass balance', count: ledgerStreamCount(view) });
  tabs.push({ id: 'chain', label: 'Mine to shipped', count: allChainReads(view).map((read) => read.element).join(' · ') });
  if (periodClosePanel(view)) tabs.push({ id: 'close', label: 'Period close', count: periodCloseCount(view) });
  return tabs;
}

/** The tab on screen: the requested one when this persona and period carry
 *  it, else the persona's default (GM → Value, everyone else → Mass balance). */
export function resolveMetalAccountingTab(
  requested: string | null,
  tabs: readonly SurfacePageTabItem<MetalAccountingTab>[],
): MetalAccountingTab {
  if (isMetalAccountingTab(requested) && tabs.some((tab) => tab.id === requested)) return requested;
  return tabs[0]!.id;
}
